﻿import AddIcon from "@mui/icons-material/Add";
import MessageIcon from "@mui/icons-material/Message";
import ShareIcon from "@mui/icons-material/Share";
import { Theme } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import { skipToken } from "@reduxjs/toolkit/query";
import { MarketplaceResultType } from "config";
import MarketplaceChatCardContent from "marketplace/_views/marketplace-chat-card-content";
import TeamResultMembers from "marketplace/_views/team-result-members";
import TeamBookmarkButton from "marketplace/bookmarks/_views/team-bookmark-button";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import { MarketplaceIndividualInfoJSON } from "marketplace/_schema/marketplace-individual-info-schema";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import { useGetTeamAvatarQuery, useContactTeamMutation } from "marketplace/teams/_api/marketplace-team-api";
import { getTeamAvatar } from "marketplace/teams/_store/marketplace-teams-redux-slice";
import Team from "marketplace/teams/team";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import Session from "users/__session/session";
import { getIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import DirectProposalButton from "work/proposals/_views/_buttons/direct-proposal-button";

const ResultCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("xl")]: {
    display: "block",
  },
  display: compactLayout ? "block" : "flex",
  flex: "1 0 auto",
  justifyContent: "space-between",
  marginBottom: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  marginTop: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  ...(compactLayout && {
    borderColor: theme.palette.divider,
    borderRadius: theme.spacing(0.75),
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    display: "inline-block",
    overflow: "hidden",
    width: "100%",
  }),
  "&:first-of-type": {
    marginTop: 0,
  },
}));
const ResultHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  alignItems: compactLayout ? "start" : "center",
  display: "grid",
  columnGap: compactLayout ? theme.spacing(1.25) : theme.spacing(2),
  gridTemplateColumns: compactLayout ? `${theme.spacing(8)} 1fr` : "auto 1fr",
}));
const ResultCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  flexGrow: "inherit",
  padding: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  paddingLeft: compactLayout ? theme.spacing(1.5) : theme.spacing(3),
  "&:last-child": {
    paddingBottom: compactLayout ? theme.spacing(1.5) : undefined,
  },
}));
const ResultCardActions = styled(CardActions, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("xl")]: {
    alignContent: "flex-end",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("lg")]: {
    alignContent: "flex-start",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  alignContent: "center",
  alignItems: compactLayout ? "unset" : "flex-end",
  flex: 0,
  flexDirection: compactLayout ? "row" : "column",
  flexWrap: "wrap",
  justifyContent: compactLayout ? "flex-start" : "space-evenly",
  marginBottom: compactLayout ? theme.spacing(2) : undefined,
  padding: compactLayout ? theme.spacing(0, 1.5, 1.5) : undefined,
  paddingRight: compactLayout ? theme.spacing(1.5) : theme.spacing(3),
}));
const ResultName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  cursor: "pointer",
  fontSize: compactLayout ? theme.typography.subtitle1.fontSize : undefined,
  fontWeight: compactLayout ? theme.typography.fontWeightBold : undefined,
  lineHeight: compactLayout ? 1.2 : undefined,
  textDecoration: "underline",
}));
const TypeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  borderColor: "#0a0",
  borderStyle: "dashed",
  color: "#0a0",
  marginTop: compactLayout ? theme.spacing(0.75) : undefined,
  opacity: 0.66,
  width: "fit-content",
}));
const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: compactLayout ? theme.spacing(0.75) : theme.spacing(1),
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginRight: compactLayout ? theme.spacing(0.75) : 0,
  minWidth: compactLayout ? 0 : theme.spacing(30),
}));
const StyledNetworkButton = styled(NetworkButton, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: compactLayout ? theme.spacing(0.75) : theme.spacing(1),
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginRight: compactLayout ? theme.spacing(0.75) : 0,
  minWidth: compactLayout ? 0 : theme.spacing(30),
}));
const StyledBookmarkButton = styled(TeamBookmarkButton, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: compactLayout ? theme.spacing(0.75) : theme.spacing(1),
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginRight: compactLayout ? theme.spacing(0.75) : 0,
  minWidth: compactLayout ? 0 : theme.spacing(30),
}));
const Subtitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  color: compactLayout ? theme.palette.text.secondary : undefined,
  fontSize: compactLayout ? theme.typography.body2.fontSize : undefined,
  fontWeight: compactLayout ? theme.typography.fontWeightBold : theme.typography.fontWeightMedium,
  letterSpacing: compactLayout ? 0.2 : undefined,
  lineHeight: compactLayout ? 1.3 : undefined,
  marginBottom: compactLayout ? theme.spacing(0.75) : undefined,
}));
const Details = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  [theme.breakpoints.up(1920)]: {
    columnCount: 2,
  },
  [theme.breakpoints.down(1920)]: {
    columnCount: 1,
  },
  [theme.breakpoints.down("xl")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("lg")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("md")]: {
    columnCount: 1,
  },
  columnCount: compactLayout ? "unset" : undefined,
  columnGap: theme.spacing(2),
  columnFill: "auto",
  display: compactLayout ? "flex" : undefined,
  flexDirection: compactLayout ? "column" : undefined,
  marginTop: compactLayout ? theme.spacing(1.5) : theme.spacing(2),
  ...(compactLayout && {
    paddingTop: 0,
    "& .MuiLink-root": {
      display: "block",
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1.35,
      marginBottom: theme.spacing(0.1),
      textUnderlineOffset: "2px",
    },
  }),
}));
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
})) as typeof Link;
const SmallAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  cursor: "pointer",
  height: compactLayout ? theme.spacing(8) : theme.spacing(10),
  width: compactLayout ? theme.spacing(8) : theme.spacing(10),
}));
const ResultAvatarCardMedia = styled(CardMedia)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
})) as typeof CardMedia;
const ResultAvatar = styled(Avatar)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
}));
const GridRow = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  display: "grid",
  borderBottom: compactLayout ? `1px solid ${theme.palette.divider}` : undefined,
  gap: compactLayout ? 0 : 0,
  gridTemplateColumns: compactLayout ? "1fr" : "1fr 1fr",
  marginBottom: compactLayout ? 0 : theme.spacing(1),
  paddingBottom: compactLayout ? theme.spacing(1.5) : 0,
  paddingTop: compactLayout ? theme.spacing(1.5) : 0,
  ...(compactLayout && {
    marginLeft: 0,
    marginRight: 0,
  }),
  "&:last-child": {
    borderBottom: "none",
  },
}));
const SelectTeamButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  marginLeft: compactLayout ? 0 : theme.spacing(2),
  marginTop: compactLayout ? theme.spacing(1) : 0,
  width: compactLayout ? "100%" : undefined,
  "&.MuiButton-root": {
    width: compactLayout ? "100%" : undefined,
  },
}));
const NameContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ theme, compactLayout }) => ({
  display: "flex",
  alignItems: compactLayout ? "flex-start" : undefined,
  flexDirection: compactLayout ? "column" : "row",
  flexWrap: "wrap",
  gap: compactLayout ? theme.spacing(0.5) : 0,
}));
const HeaderText = styled("span", {
  shouldForwardProp: (prop) => prop !== "compactLayout",
})<{ compactLayout?: boolean }>(({ compactLayout }) => ({
  minWidth: 0,
  width: "100%",
  ...(compactLayout && {
    display: "flex",
    flexDirection: "column",
  }),
}));

export interface TeamResultProps {
  result: MarketplaceTeamInfoAPIResponse;
  embedded: boolean;
  session: Session;
  navigate?: NavigateFunction;
  compactLayout?: boolean;
  getText: (fieldNames: string[], base: unknown, noCommas?: boolean) => string;
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth?: boolean,
  ) => void;
  onTeamSelected?: (team: Team) => void;
}

export default function TeamResult(props: Readonly<TeamResultProps>) {
  const { result, embedded, session, navigate, compactLayout, getText, handleResultClicked, onTeamSelected } = props;

  const teamId = result?.id ? new Guid(result.id) : null;
  const leaderId = result?.leader?.userId ? new Guid(result.leader.userId) : null;
  const avatarId = result.avatarId;

  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();

  const isXlDownDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isXlUpDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  // #region Selectors
  const cachedAvatar = useSelector(getTeamAvatar(teamId, "medium"));
  const { data: fetchedAvatar } = useGetTeamAvatarQuery(
    teamId && avatarId && !cachedAvatar ? { teamId: teamId.value, size: "medium" } : skipToken,
  );
  const avatar = cachedAvatar ?? fetchedAvatar;
  const [triggerContactTeam] = useContactTeamMutation();
  const isNetworkedWithLeader = useSelector(getIsNetworkedWith(leaderId ?? undefined));

  function openTeamProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(event, profileId, MarketplaceResultType.Team, <ViewTeamProfile teamId={profileId} />, true);
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function getProfileLink(
    type: MarketplaceResultType,
    fields: string[],
    base: unknown,
    profileId: string | null | undefined,
    noCommas: boolean = false,
    secondary: boolean = false,
  ) {
    if (!profileId) return null;
    return (
      <StyledLink
        style={{
          display: compactLayout || secondary ? "block" : undefined,
          fontSize: secondary ? "0.85em" : "1em",
          opacity: secondary ? 0.6 : 1,
        }}
        component={Typography}
        onClick={(event) => {
          if (type === MarketplaceResultType.Individual) return openIndividualProfile(event, profileId);
          if (type === MarketplaceResultType.Company) return openCompanyProfile(event, profileId);
          openTeamProfile(event, profileId);
        }}
      >
        {getText(fields, base, noCommas)}
      </StyledLink>
    );
  }

  const leaderProfileLink = getProfileLink(
    MarketplaceResultType.Individual,
    ["firstName", "lastName"],
    result?.leader,
    result?.leader?.id,
    true,
  );
  const leaderCompanyProfileLink = getProfileLink(
    MarketplaceResultType.Company,
    ["companyName"],
    result?.leader,
    result?.leader?.companyId,
    true,
    true,
  );
  const memberProfileLink = (member: MarketplaceIndividualInfoJSON) =>
    getProfileLink(MarketplaceResultType.Individual, ["firstName", "lastName"], member, member?.id, true);
  const memberCompanyProfileLink = (member: MarketplaceIndividualInfoJSON) =>
    getProfileLink(MarketplaceResultType.Company, ["companyName"], member, member?.companyId, true, true);

  const description = getText(["description"], result);
  const shortDescription = description.length > 250 ? `${description.substring(0, 250)}...` : description;

  async function contactTeam() {
    if (!result?.id) throw new Error("teamId is required to contact team.");
    try {
      const forumJson = await triggerContactTeam({ teamId: result.id }).unwrap();
      setForum(Forum.fromJSON(forumJson));
      setShowChat(true);
    } catch (error) {
      console.error(error);
    }
  }

  function getAddButton(result: MarketplaceTeamInfoAPIResponse) {
    if (!onTeamSelected) return null;

    return (
      <SelectTeamButton
        compactLayout={compactLayout}
        variant="outlined"
        color="primary"
        aria-label="Select Team"
        startIcon={<AddIcon />}
        disableElevation
        onClick={() => onTeamSelected(result.deserializeToTeam())}
      >
        Select Team
      </SelectTeamButton>
    );
  }

  function showProposalButtons(): boolean {
    if (!navigate) return false;
    return result?.id !== undefined && result?.leader?.userId !== session?.user?.id?.value;
  }

  function handleSendProposalToResult(targetUrl: URL): void {
    navigate?.(targetUrl.pathname + targetUrl.search);
  }

  return (
    <React.Fragment>
      {showChat && (
        <MarketplaceChatCardContent>
          <ChatWindow fillAvailableSpace forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
        </MarketplaceChatCardContent>
      )}
      {!showChat && (
        <ResultCard compactLayout={compactLayout} raised={false} variant="outlined">
          {!compactLayout && isXlUpDisplaySize && (
            <>
              <ResultAvatarCardMedia
                style={{ display: avatar ? undefined : "none" }}
                component="img"
                image={avatar}
                onClick={(event) => openTeamProfile(event, result?.id)}
              />
              <ResultAvatar
                style={{ display: avatar ? "none" : undefined }}
                variant="square"
                onClick={(event) => openTeamProfile(event, result?.id)}
              />
            </>
          )}
          <ResultCardContent compactLayout={compactLayout}>
            <ResultHeader compactLayout={compactLayout}>
              {(compactLayout || isXlDownDisplaySize) && (
                <span>
                  <SmallAvatar
                    compactLayout={compactLayout}
                    style={{ display: avatar ? undefined : "none" }}
                    variant="circular"
                    src={avatar}
                    onClick={(event) => openTeamProfile(event, result?.id)}
                  />
                  <SmallAvatar
                    compactLayout={compactLayout}
                    style={{ display: avatar ? "none" : undefined }}
                    variant="circular"
                    onClick={(event) => openTeamProfile(event, result?.id)}
                  />
                </span>
              )}
              <HeaderText compactLayout={compactLayout}>
                <NameContainer compactLayout={compactLayout}>
                  <ResultName
                    compactLayout={compactLayout}
                    variant="h5"
                    onClick={(event) => openTeamProfile(event, result?.id)}
                  >
                    {getText(["name"], result, true)}
                  </ResultName>
                  {!compactLayout && embedded && getAddButton(result)}
                </NameContainer>
                <TypeChip compactLayout={compactLayout} label="Team" variant="outlined" size="small" />
              </HeaderText>
            </ResultHeader>
            {compactLayout && embedded && getAddButton(result)}
            <Details compactLayout={compactLayout}>
              {result?.description && (
                <GridRow compactLayout={compactLayout}>
                  <Subtitle compactLayout={compactLayout} variant="body1">About Us</Subtitle>
                  <Typography variant="body2">{shortDescription}</Typography>
                </GridRow>
              )}
              {result?.leader?.firstName && result?.leader?.lastName && (
                <GridRow compactLayout={compactLayout}>
                  <Subtitle compactLayout={compactLayout} variant="body1">Leader</Subtitle>
                  {leaderProfileLink} {leaderCompanyProfileLink}
                </GridRow>
              )}
              <GridRow compactLayout={compactLayout}>
                <Subtitle compactLayout={compactLayout} variant="body1">{`${result.members?.length.toString() ?? "0"} Members`}</Subtitle>
                <TeamResultMembers
                  members={result?.members}
                  compactLayout={compactLayout}
                  renderMember={(member) => (
                    <>
                      {memberProfileLink(member)} {memberCompanyProfileLink(member)}
                    </>
                  )}
                />
              </GridRow>
            </Details>
          </ResultCardContent>
          {!embedded && (
            <ResultCardActions compactLayout={compactLayout} disableSpacing>
              {leaderId && <StyledNetworkButton compactLayout={compactLayout} userId={leaderId} />}
              <StyledBookmarkButton compactLayout={compactLayout} teamId={result.id ? new Guid(result.id) : undefined} />
              <Tooltip
                title={isNetworkedWithLeader ? "" : "You must be in a vendor-team's network before contacting them"}
              >
                <div style={{ display: "contents", width: "100%" }}>
                  <StyledButton
                    compactLayout={compactLayout}
                    startIcon={<MessageIcon />}
                    variant="outlined"
                    disabled={!isNetworkedWithLeader}
                    onClick={contactTeam}
                  >
                    Send Message
                  </StyledButton>
                </div>
              </Tooltip>
              <StyledButton
                compactLayout={compactLayout}
                aria-label="share"
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={() => {
                  navigator.clipboard
                    .writeText(`${globalThis.location.origin}/marketplace/team/${result?.id}`)
                    .then(() => {
                      enqueueSnackbar("Link copied to clipboard", {
                        variant: "info",
                      });
                    })
                    .catch(() => {
                      enqueueSnackbar("Failed to copy link to clipboard", {
                        variant: "error",
                      });
                    });
                }}
              >
                Share
              </StyledButton>
              {showProposalButtons() && (
                <>
                  <DirectProposalButton
                    targetName={Name.create({
                      params: { firstName: result.leader?.firstName ?? "", lastName: result.leader?.lastName ?? "" },
                    })}
                    targetTeamId={result.id ? new Guid(result.id) : undefined}
                    targetTeamName={result?.name}
                    onClick={handleSendProposalToResult}
                    useStyledButton={true}
                    action={"create"}
                  />
                  <DirectProposalButton
                    targetName={Name.create({
                      params: { firstName: result.leader?.firstName ?? "", lastName: result.leader?.lastName ?? "" },
                    })}
                    targetTeamId={result.id ? new Guid(result.id) : undefined}
                    targetTeamName={result?.name}
                    onClick={handleSendProposalToResult}
                    useStyledButton={true}
                    action={"request"}
                  />
                </>
              )}
            </ResultCardActions>
          )}
        </ResultCard>
      )}
    </React.Fragment>
  );
}
